import React, { useMemo, useState } from "react";

const WorkForm = ({
  form,
  handleChange,
  onAddService,
  onRemoveService,
  onServiceAmountChange,
  onServiceMemberChange,
  onServiceStatusChange,
  onConvertToInvoice,
  onCreateFollowUp,
  onCloseAppointment,
  onSelectCustomer,
  isEditing,
  isReadOnly,
  readOnlyMessage,
  branches,
  teamMembers,
  services,
  clients,
  selectedServices,
  timeSlots,
  onSubmit,
  onClose,
  onDelete,
  canDelete
}) => {
  const [serviceSearch, setServiceSearch] = useState("");
  const [customerFocused, setCustomerFocused] = useState(false);

  const filteredServices = useMemo(() => {
    const query = serviceSearch.trim().toLowerCase();
    return services.filter(service => {
      const ref = service.publicId || String(service.id);
      if (form.services.includes(ref)) return false;
      if (!query) return true;
      const haystack = `${service.name || ""} ${service.category || ""} ${service.treatmentType || ""}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [services, serviceSearch, form.services]);

  const filteredClients = useMemo(() => {
    const query = String(form.customer || "").trim().toLowerCase();
    if (!query) return [];
    return (clients || []).filter(client => {
      const fullName = `${client.firstName || ""} ${client.lastName || ""}`.trim().toLowerCase();
      const mobile = String(client.mobile || "").toLowerCase();
      return fullName.includes(query) || mobile.includes(query);
    }).slice(0, 8);
  }, [clients, form.customer]);

  return (
    <form className="work-form" onSubmit={onSubmit}>
      <h2 className="work-form-title">{isEditing ? "Edit Appointment" : "Create Appointment"}</h2>
      {isReadOnly && !!readOnlyMessage && (
        <div className="converted-banner">{readOnlyMessage}</div>
      )}

      <div className="schedule-top-row">
        <select
          name="branchPublicId"
          value={form.branchPublicId || ""}
          disabled={isReadOnly}
          onChange={handleChange}
          required
        >
          <option value="">Select branch</option>
          {branches.map(branch => (
            <option key={branch.publicId} value={branch.publicId}>
              {[branch.branchCode, branch.branchName].filter(Boolean).join(" - ")}
            </option>
          ))}
        </select>

        <input
          type="date"
          name="date"
          value={form.date}
          disabled={isReadOnly}
          onChange={handleChange}
          required
        />

        <select name="start" value={form.start} onChange={handleChange} disabled={isReadOnly} required>
          <option value="">Select time slot</option>
          {timeSlots.map(slot => <option key={slot} value={slot}>{slot}</option>)}
        </select>

        <input
          type="text"
          name="end"
          value={form.end}
          readOnly
          placeholder="Expected end"
        />
      </div>

      <div className="work-form-summary">
        <div><strong>Total Duration:</strong> {form.totalDuration || 0} mins</div>
        <div><strong>Expected Total:</strong> {Number(form.expectedTotal || 0).toFixed(2)}</div>
      </div>

      <label className="walk-in-checkbox">
        <input
          type="checkbox"
          name="isWalkIn"
          checked={form.isWalkIn}
          disabled={isReadOnly}
          onChange={handleChange}
        />
        Walk-in customer (skip customer details)
      </label>

      {!form.isWalkIn && (
        <div className="customer-contact-row">
          <div className="customer-field-wrapper">
            <input
              type="text"
              name="customer"
              placeholder="Customer name (type name/mobile to search existing customers)"
              value={form.customer}
              disabled={isReadOnly}
              onChange={handleChange}
              onFocus={() => setCustomerFocused(true)}
              onBlur={() => {
                setTimeout(() => setCustomerFocused(false), 120);
              }}
              required={!form.isWalkIn}
              autoComplete="off"
            />
              {customerFocused && String(form.customer || "").trim() && (
              <div className="customer-search-list">
                {filteredClients.length === 0 ? (
                  <div className="service-search-empty">No customer found.</div>
                ) : (
                  filteredClients.map(client => {
                    const id = client.publicId || String(client.id || `${client.firstName || ""}-${client.lastName || ""}-${client.mobile || ""}`);
                    const fullName = `${client.firstName || ""} ${client.lastName || ""}`.trim() || "Customer";
                    return (
                      <button
                        key={id}
                        type="button"
                        className="customer-search-item"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => {
                          onSelectCustomer(client);
                          setCustomerFocused(false);
                        }}
                      >
                        <span>{fullName}</span>
                        <small>{client.mobile || "-"}</small>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
          <input
            type="text"
            name="contact"
            placeholder="Customer contact"
            value={form.contact}
            disabled={isReadOnly}
            onChange={handleChange}
          />
        </div>
      )}

      <div className="service-picker">
        <h3>Services ({selectedServices.length} selected)</h3>
        {services.length === 0 ? (
          <p className="service-picker-empty">No services found. Add services in Service Master first.</p>
        ) : (
          <>
            <input
              type="text"
              className="service-search-input"
              placeholder="Search service by name, category, treatment"
              value={serviceSearch}
              disabled={isReadOnly}
              onChange={event => setServiceSearch(event.target.value)}
            />

            <div className="service-grid">
              {filteredServices.length === 0 ? (
                <div className="service-search-empty">No matching services.</div>
              ) : (
                filteredServices.map(service => {
                  const ref = service.publicId || String(service.id);
                  return (
                    <div key={ref} className="service-item" onClick={() => { if (!isReadOnly) onAddService(ref); }} role="button" tabIndex={0} onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && !isReadOnly) onAddService(ref); }}>
                      <div className="service-search-details">
                        <strong>{service.name}</strong>
                        <small>
                          {Number(service.durationMinutes || 30)} mins | {service.category || "General"} | {service.treatmentType || "Standard"} | {Number(service.price || 0).toFixed(2)}
                        </small>
                      </div>
                      <button
                        type="button"
                        className="service-add-btn"
                        disabled={isReadOnly}
                        onClick={e => { e.stopPropagation(); onAddService(ref); }}
                      >
                        + Add
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            <div className="selected-services" aria-label="Selected services">
              {selectedServices.length === 0 ? (
                <div className="service-search-empty">Add at least one service.</div>
              ) : (
                selectedServices.map(service => {
                  const ref = service.publicId || String(service.id);
                  const value = form.serviceAmounts?.[ref] ?? service.price ?? 0;
                  const selectedMemberPublicId = String(form.serviceMembers?.[ref] || "");
                  const status = form.serviceStatuses?.[ref] || "pending";
                  const isDone = status === "done";
                  const isCarryForward = status === "carry_forward";
                  const statusLabel = isReadOnly
                    ? (isDone ? "Done" : (isCarryForward ? "Carry Forward" : "Pending"))
                    : (isDone ? "✓ Done" : (isCarryForward ? "Carry Forward" : "Pending"));
                  return (
                    <div key={ref} className={`selected-service-chip${isDone ? " service-chip-done" : ""}${isCarryForward ? " service-chip-carry-forward" : ""}`}>
                      <span className="selected-service-label">
                        {service.name} ({Number(service.durationMinutes || 30)}m)
                      </span>
                      <input
                        type="number"
                        className="service-amount-input"
                        min="0"
                        step="0.01"
                        value={value}
                        disabled={isReadOnly}
                        onChange={event => onServiceAmountChange(ref, event.target.value)}
                      />
                      <select
                        className="service-member-select"
                        value={selectedMemberPublicId}
                        disabled={isReadOnly}
                        onChange={event => onServiceMemberChange(ref, event.target.value)}
                        required
                      >
                        <option value="">Assign member</option>
                        {teamMembers.map(member => (
                          <option key={member.publicId} value={member.publicId}>{member.name}</option>
                        ))}
                      </select>
                      {isEditing && isReadOnly && (
                        <button
                          type="button"
                          className={`service-status-btn${isDone ? " done" : ""}${isCarryForward ? " carry-forward" : ""}`}
                          disabled
                          title={`Status: ${statusLabel}`}
                        >
                          {statusLabel}
                        </button>
                      )}
                      {isEditing && !isReadOnly && (
                        <>
                          <button
                            type="button"
                            className={`service-status-btn${isDone ? " done" : ""}`}
                            onClick={() => onServiceStatusChange(ref, isDone ? "pending" : "done")}
                            title={isDone ? "Mark as Pending" : "Mark as Done"}
                          >
                            {isDone ? "✓ Done" : "Mark Done"}
                          </button>
                          <button
                            type="button"
                            className={`service-status-btn carry-forward${isCarryForward ? " active" : ""}`}
                            onClick={() => onServiceStatusChange(ref, isCarryForward ? "pending" : "carry_forward")}
                            title={isCarryForward ? "Clear Carry Forward" : "Mark as Carry Forward"}
                          >
                            {isCarryForward ? "Carry Forward" : "Mark Carry Forward"}
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        className="chip-remove-btn"
                        disabled={isReadOnly}
                        onClick={() => onRemoveService(ref)}
                        aria-label={`Remove ${service.name}`}
                      >
                        x
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>

      <div className="work-form-actions">
        {!isReadOnly && <button type="submit">{isEditing ? "Update Appointment" : "Save Appointment"}</button>}
        {!isReadOnly && isEditing && onConvertToInvoice && (
          <button type="button" className="convert-invoice-btn" onClick={onConvertToInvoice}>Convert to Invoice</button>
        )}
        {isEditing && onCreateFollowUp && (
          <button type="button" className="follow-up-btn" onClick={onCreateFollowUp}>Create Follow-up Appointment</button>
        )}
        {!isReadOnly && isEditing && onCloseAppointment && (
          <button type="button" className="close-appointment-btn" onClick={onCloseAppointment}>Close Appointment</button>
        )}
        {!isReadOnly && isEditing && canDelete && (
          <button type="button" className="delete-btn" onClick={onDelete}>Delete Appointment</button>
        )}
        <button type="button" className="secondary-btn" onClick={onClose}>Close</button>
      </div>
    </form>
  );
};

export default WorkForm;
